import React, { useEffect, useState } from 'react';
import log from 'loglevel';
import { useServices } from '@/components/ServiceContext.tsx';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { downloadFileFromS3 } from '@/utils/downloadUtils';

const DownloadExport: React.FC = () => {
  const { exportJobService } = useServices();
  const { exportId } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const download = async (exportId: string) => {
      try {
        const { items: exportJobs } = await exportJobService.search({
          filter: {
            id: { eq: exportId },
          },
        });

        if (exportJobs.length === 0) {
          throw new Error('Export does not exist or has expired.');
        }

        const { exportName, s3Key, outputFormat } = exportJobs[0];

        if (!s3Key) {
          throw new Error('No S3 key provided for download.');
        }

        await downloadFileFromS3(exportName, s3Key, outputFormat || undefined);
        setStatus('success');
      } catch (error: unknown) {
        log.error('Error downloading export file:', error);
        if (error instanceof Error) {
          setErrorMessage(error.message || 'An unexpected error occurred.');
        }
        setStatus('error');
      }
    };

    if (!exportId) {
      const message = 'No export ID provided.';
      log.error(message);
      setErrorMessage(message);
      setStatus('error');
      return;
    }

    download(exportId);
  }, [exportJobService, exportId, navigate]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <Box display="flex" flexDirection="column" alignItems="center" mt={4}>
            <CircularProgress />
            <Typography variant="h6" align="center" mt={2}>
              Downloading your file, please wait...
            </Typography>
          </Box>
        );
      case 'success':
        return (
          <Box display="flex" flexDirection="column" alignItems="center" mt={4}>
            <Alert severity="success">
              Your file has been downloaded successfully.
            </Alert>
          </Box>
        );
      case 'error':
        return (
          <Box display="flex" flexDirection="column" alignItems="center" mt={4}>
            <Alert severity="error">Error: {errorMessage}</Alert>
          </Box>
        );
      default:
        return null;
    }
  };

  return <div>{renderContent()}</div>;
};

export default DownloadExport;
